import React, { useState, useCallback, useRef, useEffect } from "react";
import { Container, styled, Hidden } from "@material-ui/core";
import FullPageMap from "./components/Map/FullPageMap";
import List from "./components/List";
import TopBar from "./components/TopBar";
import ActiveFilters from "./components/ActiveFilters";
import {
  getAllLabelsFromStores,
  useFilteredStores,
  useMapGeolocation
} from "./utils";
import { ListRefType } from "./components/List";
import {
  MOBILE_DEFAULT_VIEWMODE,
  MobileViewmodeType,
  StoreType,
  TOP_BAR_HEIGHT,
  TOP_BAR_HEIGHT_MOBILE,
  DESKTOP_LIST_WIDTH_POURC
} from "./constants";
import useIsMobile from "../../hooks/use-is-mobile";

const MainContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "row",
  alignItems: "stretch",
  flexShrink: 0,
  // Max height is needed to prevent the whole page from scrolling, and only make the left list scrollable
  [theme.breakpoints.up("md")]: {
    // prettier-ignore
    maxHeight: `calc(100vh - var(--header-height) - ${TOP_BAR_HEIGHT} - env(safe-area-inset-top))`
  },
  [theme.breakpoints.down("sm")]: {
    maxHeight: `calc(100% - ${TOP_BAR_HEIGHT_MOBILE})`
  }
}));

const LeftColumn = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    minWidth: "none",
    width: "100%"
  },
  [theme.breakpoints.up("md")]: {
    overflowY: "auto", // Scroll left list on desktop
    minWidth: `${DESKTOP_LIST_WIDTH_POURC}%`,
    paddingRight: theme.spacing(2)
  }
}));

type Props = {
  stores: StoreType[];
};

const StoreLocator = ({ stores }: Props) => {
  const storeLabels = getAllLabelsFromStores(stores);
  const filteredStores = useFilteredStores(stores);
  const isFilteringStores = filteredStores.length < stores.length;

  const [selectedStore, setSelectedStore] = useState<StoreType | null>(null);
  const [hoveredStore, setHoveredStore] = useState<StoreType | null>(null);

  const { mapRef, geolocationState, requestGeolocation } = useMapGeolocation();
  const listRef = useRef<ListRefType>();

  const isMobile = useIsMobile();
  const [mobileViewmode, setMobileViewmode] = useState<MobileViewmodeType>(
    MOBILE_DEFAULT_VIEWMODE
  );

  // Switch to map when clicking "Use my location" on mobile
  const handleGeolocationClick = useCallback(() => {
    if (isMobile && mobileViewmode !== "map") {
      setMobileViewmode("map");
    }
    requestGeolocation();
  }, [isMobile, mobileViewmode, setMobileViewmode, requestGeolocation]);

  // Switch to map mode with selected store when clicking store card on mobile
  const handleListStoreCardClick = useCallback(
    (store: StoreType) => {
      if (isMobile && store && mobileViewmode !== "map") {
        setMobileViewmode("map");
      }
      setSelectedStore(store);
    },
    [setMobileViewmode, setSelectedStore, isMobile, mobileViewmode]
  );

  // Auto scroll to selected card when switching back to "List" view on mobile
  useEffect(() => {
    if (
      isMobile &&
      mobileViewmode === "list" &&
      selectedStore &&
      listRef.current &&
      listRef.current.scrollToStore
    ) {
      listRef.current.scrollToStore(selectedStore.originalId, {
        block: "center"
      });
    }
  }, [mobileViewmode]);

  return (
    <>
      <TopBar
        labels={storeLabels}
        mobileViewmode={mobileViewmode}
        onMobileViewmodeChange={setMobileViewmode}
        geolocationState={geolocationState}
        onGeolocationClick={handleGeolocationClick}
      />
      <MainContainer maxWidth="xl">
        <Hidden smDown={mobileViewmode !== "list"}>
          <LeftColumn>
            <ActiveFilters labels={storeLabels} />
            <List
              stores={filteredStores}
              selectedStore={selectedStore}
              onSelectedStoreChange={handleListStoreCardClick}
              onHoveredStoreChange={setHoveredStore}
              isMobile={isMobile}
              ref={listRef}
              marginTop={isMobile && isFilteringStores ? 0 : 4}
              marginBottom={{ xs: 2, md: 4 }}
            />
          </LeftColumn>
        </Hidden>
        <Hidden smDown={mobileViewmode !== "map"}>
          <FullPageMap
            stores={filteredStores}
            selectedStore={selectedStore}
            onSelectedStoreChange={setSelectedStore}
            hoveredStore={hoveredStore}
            isMobile={isMobile}
            ref={mapRef}
            zoomToMarkerOffset={isMobile ? { latitude: 0.05 } : null}
          />
        </Hidden>
      </MainContainer>
    </>
  );
};

export default StoreLocator;
